import { FC, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Select,
  Tooltip,
  TooltipProps,
  styled,
  tooltipClasses
} from '@mui/material';
import MDBox from '@components/MDBox';
import MDTypography from '@components/MDTypography';
import DataTable from '@components/Tables/DataTable';

import MDButton from '@components/MDButton';
import {
  useAcceptAndImportProjectMutation,
  useLazyGetCleanPageDataQuery,
  useUpdateCleanActionMutation
} from '@api/projects';
import { useParams } from 'react-router-dom';
import FullPageLoader from '@components/FullPageLoader';
import FaIcon from '@components/FaIcon';
import {
  DIRT_TYPES,
  getCleanLabel,
  getFontBorderColor,
  getLabelColor,
  getLabelCount,
  getTableFooterText
} from '@utils/cleaning';
import { Dirtcolumn, Dirtrow } from '@DStypes/project';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@store/store';
import { setCleanPageData } from '@pages/ProjectBoard/slice';
import useCustomNavigate from '@hooks/useCustomNavigate';
import useToast from '@hooks/useToast';
import { parseJSON } from '@utils/common';
import React from 'react';
import pxToRem from '@theme/functions/pxToRem';
import { ColumnTypes } from '@constants/common';
import { sendAmplitudeData } from '@utils/amplitude';
import {
  CLICKED_KNOW_MORE,
  FILE_REVIEW,
  PAGE_VISITED
} from '@constants/eventNames';
import { sendGAData } from '@utils/googleAnalytics';
import CloseButton from '@components/CloseButton';

interface Props {
  open: boolean;
  onClose: () => void;
  projectName: string;
  fileType: string;
  fileId: string;
  dataTypeChange: string;
  anonymisationChange: string;
}

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    boxShadow: theme.shadows[4]
  }
}));

const getColumnFacts = (fieldType: string, dataFact: any) => {
  if (dataFact) {
    const textString = `First In List is ${dataFact?.min} and Last In List is ${dataFact?.max}`;
    const dateString = `Is between ${dataFact?.min} and  ${dataFact?.max}`;
    const defaultString = `Range is from ${dataFact?.min} to ${dataFact?.max}`;

    switch (fieldType) {
      case ColumnTypes.TEXT:
        return textString;
      case ColumnTypes.DATE:
      case ColumnTypes.DATETIME:
        return dateString;
      case ColumnTypes.EMPTY:
        return '';
      default:
        return defaultString;
    }
  } else {
    return <></>;
  }
};

const CleanPage: FC<Props> = ({
  open,
  onClose,
  projectName,
  fileType,
  fileId,
  dataTypeChange,
  anonymisationChange
}) => {
  const { projectId } = useParams();

  const dispatch = useDispatch();
  const navigate = useCustomNavigate();

  const cleanPageData = useSelector(
    (state: RootState) => state?.projectBoard?.cleanPageData
  );

  const reviewPageData = useSelector(
    (state: RootState) => state?.projectBoard?.currentProjectFieldList.fields
  );

  const [acceptAndImportProject] = useAcceptAndImportProjectMutation();
  const [getCleanPageData, { isLoading }] = useLazyGetCleanPageDataQuery();
  const [updateCleanAction] = useUpdateCleanActionMutation();

  useEffect(() => {
    sendAmplitudeData(PAGE_VISITED, { 'Page Name': 'Clean modal' });
  }, []);

  const { pushInfoToast } = useToast();

  const fetchCleanPageData = async () => {
    const response = await getCleanPageData({
      project_id: projectId,
      type: 'column'
    });

    if (response.data) {
      const columnsWithDirtTypes = response.data.dirt_columns.filter(
        (column) => Object.keys(column.dirt_types).length > 0
      );

      const dirtRows = response.data.dirt_rows.map(
        (row: Dirtrow) =>
          ({
            id: '',
            fieldname: '',
            data_facts: '',
            treat_as_type: '',
            dirt_types: { [row.dirt_type]: row.dirt_examples },
            cleaned_count: { [row.dirt_type]: row.dirt_quantity },
            clean_action: { [row.dirt_type]: null },
            clean_action_choices: {
              [row.dirt_type]: row.clean_action_choices
            },
            default_action_choice: {
              [row.dirt_type]: row.default_action_choice
            }
          } as Dirtcolumn)
      );

      const columnsWithoutDirtTypes = response.data.dirt_columns.filter(
        (column) => Object.keys(column.dirt_types).length === 0
      );

      const reorderedDirtColumns = columnsWithDirtTypes
        .concat(dirtRows)
        .concat(columnsWithoutDirtTypes);

      const modifiedData = {
        ...response.data,
        dirt_columns: reorderedDirtColumns
      };

      dispatch(setCleanPageData(modifiedData));

      if (
        modifiedData.dirt_columns.every(
          (column: Dirtcolumn) => Object.keys(column.dirt_types).length === 0
        )
      )
        pushInfoToast(
          'No dirty data found. Please proceed by selecting “Clean & Import”'
        );
    }
  };

  useEffect(() => {
    const importedFields = reviewPageData
      ?.filter((data: any) => data.to_import === true)
      .map((data: any) => data.fieldname);

    const cleanPageColumns = cleanPageData?.dirt_columns
      .filter((data: Dirtcolumn) => data?.fieldname !== '')
      .map((data: Dirtcolumn) => data.fieldname);

    const noRefetchFlag =
      importedFields?.length === cleanPageColumns?.length &&
      importedFields?.every((value: string) =>
        cleanPageColumns?.includes(value)
      );

    if (
      projectId &&
      (!cleanPageData ||
        cleanPageData?.project_id !== projectId ||
        !noRefetchFlag)
    )
      fetchCleanPageData();
  }, [projectId, reviewPageData, cleanPageData]);

  const handleCleanActionChange = (
    value: string,
    field_id: string,
    dirt_type: string
  ) => {
    const modifiedCleanPageData = {
      ...cleanPageData,
      dirt_columns: cleanPageData?.dirt_columns.map((column: Dirtcolumn) => {
        if (column.id === field_id)
          return {
            ...column,
            default_action_choice: {
              ...column.default_action_choice,
              [dirt_type]: value
            }
          };
        else return column;
      })
    };

    dispatch(setCleanPageData(modifiedCleanPageData));
    updateCleanAction({
      project_id: projectId,
      payload: {
        dirt_type,
        clean_action: value,
        ...(field_id && { field_id: field_id })
      }
    });
  };

  const getToolTipTable = (type: string, dirtInfo: string) => {
    const parsedData: any = parseJSON(dirtInfo);

    const tableColumns = parsedData.columns.map((column: string) => ({
      Header: column,
      accessor: column
    }));

    const escapeCharacters = ['\n', '\r', '\n\r', '\t'];

    const tableRows = parsedData.data.map((data: string[]) =>
      parsedData?.columns.reduce((acc: any, column: any, index: number) => {
        if (escapeCharacters.includes(data[index]))
          acc[column] = data[index]
            .replace(/\n/g, '\\n')
            .replace(/\r/g, '\\r')
            .replace(/\t/g, '\\t');
        else acc[column] = data[index];

        return acc;
      }, {})
    );

    return (
      <MDBox display='flex' flexDirection='column' gap={1} alignItems='center'>
        <DataTable
          table={{
            columns: tableColumns,
            rows: tableRows || []
          }}
          showTotalEntries={false}
          entriesPerPage={{
            defaultValue: tableRows?.length || 10,
            entries: []
          }}
          width='max-content'
          maxHeight='50vh'
        />
        <MDTypography
          variant='xs'
          colr='info'
          color='secondary'
          fontWeight='bold'
          textGradient
          sx={{ px: 2, mt: 1.2, mb: 0.2 }}
        >
          {getTableFooterText(type)}
        </MDTypography>
      </MDBox>
    );
  };

  const tableColumns = useMemo(
    () => [
      {
        Header: 'Column Name',
        accessor: 'fieldname',
        width: '25%',
        Cell: ({ row }: any) => (
          <Grid
            container
            display='flex'
            flexDirection='row'
            gap={0.5}
            alignItems='center'
            alignContent='center'
          >
            {row.original.contains_sensitive === true && (
              <Grid item>
                <IconButton sx={{ mb: '5px' }}>
                  <FaIcon
                    icon='user-lock'
                    typographyProps={{ variant: 'sm', color: 'primary' }}
                  />
                </IconButton>
              </Grid>
            )}
            <Tooltip
              title={getColumnFacts(
                row.original.treat_as_type,
                parseJSON(row.original.data_facts)
              )}
              placement='top'
            >
              <span style={{ wordBreak: 'break-all' }}>
                {row.original.fieldname}
              </span>
            </Tooltip>
          </Grid>
        )
      },
      {
        Header: 'Clean Types',
        accessor: 'clean_types',
        width: '25%',
        Cell: ({ row }: any) => {
          const cleanedTypes = Object.keys(row.original.cleaned_count);
          const cleanedCount: number[] = Object.values(
            row.original.cleaned_count
          );
          const cleanPageElement = document.getElementById('clean-page-modal');
          const width = cleanPageElement?.clientWidth;

          if (cleanedTypes.length)
            return (
              <MDBox display='flex' flexDirection='column' gap={1}>
                {cleanedTypes.map((type: string, index: number) => {
                  if (cleanedCount[index] > 0)
                    return (
                      <MDBox display='flex' alignItems='center'>
                        <MDBox
                          key={type}
                          sx={{
                            border: `1px solid ${getFontBorderColor(type)}`,
                            width: width ? 0.16 * width : 'fit-content',
                            paddingY: '2.5px',
                            paddingX: '5px',
                            borderRadius: '5px',
                            backgroundColor: `${getLabelColor(type)}`,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                          }}
                        >
                          <MDTypography
                            fontWeight='bold'
                            fontSize='0.7rem'
                            sx={{
                              color: getFontBorderColor(type),
                              txetAlign: 'center'
                            }}
                          >
                            {`${getCleanLabel(type)}`}
                          </MDTypography>
                          <MDTypography
                            fontWeight='bold'
                            fontSize='0.7rem'
                            sx={{
                              color: getFontBorderColor(type),
                              textAlign: 'center'
                            }}
                          >
                            {getLabelCount(type, cleanedCount[index])}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    );
                  else return <></>;
                })}
              </MDBox>
            );
          else return <>-</>;
        }
      },
      {
        Header: 'Info.',
        accessor: 'info',
        width: '5%',
        Cell: ({ row }: any) => {
          const cleanedTypes = Object.keys(row.original.cleaned_count);
          const cleanedCount: number[] = Object.values(
            row.original.cleaned_count
          );

          if (cleanedTypes.length)
            return (
              <MDBox display='flex' flexDirection='column' gap={2}>
                {cleanedTypes.map((type: string, index: number) => {
                  if (cleanedCount[index] > 0 && type !== DIRT_TYPES.EMPTY_ROWS)
                    return (
                      <LightTooltip
                        title={
                          type === DIRT_TYPES.EMPTY_ROWS
                            ? ''
                            : getToolTipTable(
                                type,
                                row.original.dirt_types[type]
                              ) || ''
                        }
                        placement='right'
                        arrow={false}
                      >
                        <span>
                          <FaIcon
                            icon={['far', 'circle-info']}
                            style={{ marginLeft: '5px' }}
                            typographyProps={{
                              color: 'secondary',
                              variant: 'lg'
                            }}
                          />
                        </span>
                      </LightTooltip>
                    );
                  else return <></>;
                })}
              </MDBox>
            );
          else return <></>;
        }
      },
      {
        Header: 'Cleaning Action',
        accessor: 'clean_action',
        width: '40%',
        Cell: ({ row }: any) => {
          const cleanTypes = Object.keys(row.original.clean_action_choices);
          const cleanedCount: number[] = Object.values(
            row.original.cleaned_count
          );

          const cleanPageElement = document.getElementById('clean-page-modal');
          const width = cleanPageElement?.clientWidth;

          if (cleanTypes.length)
            return cleanTypes.map(
              (type: string, index: number) =>
                cleanedCount[index] > 0 && (
                  <MDBox
                    key={type}
                    sx={{ my: 1, width: width ? 0.33 * width : '' }}
                  >
                    {row.original.clean_action_choices[type].length > 1 ? (
                      <Select
                        defaultValue={row.original.default_action_choice[type]}
                        sx={{
                          py: '5px',
                          display: 'block',
                          width: '100%'
                        }}
                        IconComponent={ExpandMore}
                        onChange={(e) =>
                          handleCleanActionChange(
                            e.target.value,
                            row.original.id,
                            type
                          )
                        }
                        MenuProps={{
                          anchorOrigin: {
                            vertical: 32,
                            horizontal: 0
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left'
                          },
                          sx: {
                            maxHeight: '400px',
                            width: width ? 0.31 * width : '100%'
                          }
                        }}
                      >
                        {row.original.clean_action_choices[type].map(
                          (action: string[]) => (
                            <MenuItem key={action[0]} value={action[0]}>
                              <MDTypography
                                variant='sm'
                                textTransform='none'
                                sx={{
                                  mr: 1.5,
                                  textOverflow: 'ellipsis',
                                  overflow: 'hidden',
                                  width: width ? 0.31 * width : '100%'
                                }}
                                color='secondary'
                                fontWeight='bold'
                              >
                                {action[1]}
                              </MDTypography>
                            </MenuItem>
                          )
                        )}
                      </Select>
                    ) : (
                      // Render just the selected option without the dropdown
                      <MDBox
                        sx={(theme: any) => ({
                          display: 'flex',
                          width: '100%',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          border: `1px solid ${theme.palette.secondary.main}`,
                          borderRadius: '0.375rem',
                          padding: `5px ${pxToRem(12)}`
                        })}
                      >
                        <MDTypography
                          variant='sm'
                          color='secondary'
                          textTransform='none'
                          fontWeight='bold'
                          sx={{ textOverflow: 'ellipsis', overflow: 'hidden' }}
                        >
                          {row.original.clean_action_choices[type][0][1]}
                        </MDTypography>
                      </MDBox>
                    )}
                  </MDBox>
                )
            );
          else return <></>;
        }
      },
      {
        Header: '',
        accessor: 'status_icon',
        width: '5%',
        Cell: ({ row }: any) => {
          const dirtTypes = Object.keys(row.original.dirt_types);

          if (dirtTypes.length === 0)
            return <FaIcon icon='check' style={{ color: 'green' }} />;
          else
            return (
              <MDBox display='flex' flexDirection='column' gap={2}>
                {dirtTypes.map((type: string) => {
                  const defaultActionChoice =
                    row.original.default_action_choice[type];

                  const defaultActionLabel = row.original.clean_action_choices[
                    type
                  ]
                    .filter(
                      (clean_action: string[]) =>
                        clean_action[0] === defaultActionChoice
                    )
                    .flat();

                  if (defaultActionLabel[1]?.trim()?.endsWith('(RECOMMENDED)'))
                    return (
                      <Tooltip
                        title='Recommended action is selected'
                        placement='top'
                      >
                        <span>
                          <FaIcon
                            icon={['far', 'broom-wide']}
                            typographyProps={{
                              color: 'secondary',
                              variant: 'lg'
                            }}
                          />
                        </span>
                      </Tooltip>
                    );
                  else
                    return (
                      <Tooltip
                        title='You have selected an action different than the Recommendation'
                        placement='top'
                      >
                        <span>
                          <FaIcon
                            icon={['fas', 'broom-wide']}
                            typographyProps={{
                              color: 'secondary',
                              variant: 'lg'
                            }}
                          />
                        </span>
                      </Tooltip>
                    );
                })}
              </MDBox>
            );
        }
      }
    ],
    [cleanPageData]
  );

  if (isLoading || !cleanPageData || cleanPageData?.project_id !== projectId)
    return <FullPageLoader />;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      sx={{
        zIndex: 100,
        '& .MuiPaper-root': {
          width: '90%',
          maxWidth: '90% !important',
          position: 'relative',
          paddingX: '10px',
          maxHeight: '80%',
          overflow: 'visible'
        }
      }}
      id='clean-page-modal'
    >
      <CloseButton onClose={() => onClose()} />
      <DialogContent sx={{ mb: 2 }}>
        <Grid
          container
          spacing={2}
          alignItems='center'
          justifyItems='center'
          mb={2}
        >
          <Grid item>
            <MDTypography
              variant='sm'
              color='info'
              sx={{
                textTransform: 'uppercase',
                fontWeight: '800'
              }}
            >
              CLEAN
            </MDTypography>
          </Grid>
          <Grid item>
            <MDTypography variant='sm' color='info' fontWeight='regular'>{`${
              cleanPageData?.file?.imported_rows ||
              cleanPageData?.file?.file_rows
            }  Rows / ${
              cleanPageData?.file?.imported_columns ||
              cleanPageData?.file?.file_columns
            } Columns`}</MDTypography>
          </Grid>
        </Grid>

        <Grid
          container
          display='flex'
          alignItems='flex-start'
          flexWrap='nowrap'
          gap={1}
          mb={2}
        >
          <Grid item xs={10}>
            <MDTypography
              variant='h3'
              textGradient
              color='info'
              sx={{ wordBreak: 'break-all' }}
            >
              {projectName}
            </MDTypography>
          </Grid>
          <Grid
            item
            xs={2}
            display='flex'
            justifyContent='flex-end'
            justifyItems='flex-end'
          >
            <MDBox display='flex' flexDirection='column' gap={1}>
              <Tooltip
                title='Click to proceed with the selected Actions below'
                placement='left'
              >
                <MDButton
                  size='small'
                  color='primary'
                  variant='gradient'
                  onClick={() => {
                    acceptAndImportProject({
                      id: projectId,
                      is_auto_clean: false
                    });
                    sendAmplitudeData(FILE_REVIEW, {
                      'File Type': fileType,
                      'File ID': fileId,
                      'Name of the file/project': projectName,
                      'Change in Data Type': dataTypeChange,
                      'Change in Data Anonymisation': anonymisationChange
                    });
                    sendGAData(FILE_REVIEW, {
                      'File Type': fileType,
                      'File ID': fileId,
                      'Name of the file/project': projectName,
                      'Change in Data Type': dataTypeChange,
                      'Change in Data Anonymisation': anonymisationChange
                    });
                    navigate(`/project/${projectId}/edit`);
                  }}
                >
                  <MDTypography
                    textTransform='uppercase'
                    variant='sm'
                    color='white'
                    fontWeight='bold'
                  >
                    Clean & Import
                  </MDTypography>
                </MDButton>
              </Tooltip>
              <span
                role='presentation'
                onClick={() =>
                  sendAmplitudeData(CLICKED_KNOW_MORE, {
                    'Feature Name': 'Clean Data'
                  })
                }
              >
                <MDTypography
                  variant='xs'
                  color='info'
                  sx={{
                    textAlign: 'center',
                    textUnderlineOffset: 3,
                    py: '7px'
                  }}
                  component={Link}
                  href='https://www.notion.so/datasquirrel/What-is-Data-Cleaning-e070801e111b4e9c9d3ae25e53e1708f?pvs=4'
                  target='_blank'
                  rel='noreferrer'
                  underline
                  fontWeight='bold'
                  textGradient
                >
                  Know more about Cleaning
                </MDTypography>
              </span>
            </MDBox>
          </Grid>
        </Grid>
        <MDBox>
          <DataTable
            table={{
              columns: tableColumns,
              rows: cleanPageData?.dirt_columns || []
            }}
            showTotalEntries={false}
            entriesPerPage={{
              defaultValue: cleanPageData?.dirt_columns.length || 10,
              entries: []
            }}
            customStyle={{ overflowY: 'hidden' }}
            cleanPageTable
          />
        </MDBox>
      </DialogContent>
    </Dialog>
  );
};

export default CleanPage;
